/** @format */
import { Injectable } from '@angular/core';
import { EntityState, EntityStore, QueryEntity, StoreConfig } from '@datorama/akita';
import { NgEntityServiceConfig } from '@datorama/akita-ng-entity-service';
import { OpenUniversityProductCode, OtmId } from 'common-typescript/types';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { EntityService } from './entity.service';

const CONFIG = {
    ENDPOINTS: {
        backend: '/kori/api',
    },
};

@Injectable({
    providedIn: 'root',
})
@NgEntityServiceConfig({
    baseUrl: CONFIG.ENDPOINTS.backend,
})
export class OpenUniversityProductCodeEntityService extends EntityService<OpenUniversityProductCodeState> {
    constructor() {
        super(OpenUniversityProductCodeStore, OpenUniversityProductCodeQuery);
    }

    /**
     * Get all open university product codes for a specific university.
     * Always returns an observable to the store corresponding to product codes with the given university id.
     *
     * @param universityOrgId Id of university
     * @param bypassStore Boolean whether to first fetch from REST-api or not. Defaults to true since there is no sure way to deduce whether the
     * store contains entities returned by a non-id-based request. If one is sure all needed data is already loaded, then one can set this to false.
     */
    getByUniversityOrgId(universityOrgId: string, bypassStore: boolean = true): Observable<OpenUniversityProductCode[]> {
        if (!bypassStore) {
            return this.selectAll({ filterBy: openUniversityProductCode => openUniversityProductCode.universityOrgId === universityOrgId });
        }
        return this.get({ params: { universityOrgId } }).pipe(
            switchMap(() =>
                this.selectAll({ filterBy: openUniversityProductCode => openUniversityProductCode.universityOrgId === universityOrgId }),
            ),
        );
    }
}

type OpenUniversityProductCodeState = EntityState<OpenUniversityProductCode, OtmId>;

@StoreConfig({ name: 'open-university-product-codes' })
class OpenUniversityProductCodeStore extends EntityStore<OpenUniversityProductCodeState> {}

class OpenUniversityProductCodeQuery extends QueryEntity<OpenUniversityProductCodeState> {
    constructor(protected store: OpenUniversityProductCodeStore) {
        super(store);
    }
}
